import { useEffect, useState } from 'react';
import type { PinnedObjectiveView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { objectiveCategoryTokenPrefix } from '@ui/desktop/objective-form/FormPrimitives';

/**
 * Phone-native pinned-objectives chip strip (R8.12, today-phone-parity.md
 * §3). Read-order only — no drag handle, no keyboard-move affordance of any
 * kind (order is set from desktop's sidebar reorder control or the
 * objective detail screen's pin affordance). Renders nothing at all (zero
 * height, not an empty placeholder) when no objectives are pinned, matching
 * `today-desktop-v2.md` §3's identical "absent, not a fake empty state"
 * ruling for its own sidebar section.
 */
export function PinnedObjectiveChipStrip({
  onOpenObjective,
}: {
  readonly onOpenObjective: (objectiveId: string) => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const [pinned, setPinned] = useState<readonly PinnedObjectiveView[]>([]);

  useEffect(() => {
    let cancelled = false;
    void api.listPinnedObjectives().then((rows) => {
      if (!cancelled) setPinned(rows);
    });
    return () => {
      cancelled = true;
    };
  }, [api, version]);

  if (pinned.length === 0) return null;

  return (
    <div
      role="list"
      aria-label="Pinned objectives"
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        padding: '0 var(--layout-page-margin-phone)',
        marginTop: 'var(--space-3)',
        marginBottom: 'var(--space-3)',
      }}
    >
      {pinned.map((objective) => (
        <PinnedChip
          key={objective.objectiveId}
          objective={objective}
          onOpen={() => onOpenObjective(objective.objectiveId)}
        />
      ))}
      {/* Trailing spacer so the last chip's hit-slop doesn't sit flush
          against the scroll container's own right edge while scrolled fully
          right (§3's "bleed slightly past the 16px edge" polish detail). */}
      <div aria-hidden="true" style={{ flex: 'none', width: '1px' }} />
    </div>
  );
}

function PinnedChip({
  objective,
  onOpen,
}: {
  readonly objective: PinnedObjectiveView;
  readonly onOpen: () => void;
}) {
  const tokenPrefix = objectiveCategoryTokenPrefix(objective.category);
  return (
    <button
      role="listitem"
      type="button"
      onClick={onOpen}
      aria-label={`Open ${objective.title}`}
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        minHeight: '36px',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-pill)',
        background: 'var(--surface-hover)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          flex: 'none',
          background: tokenPrefix ? `var(${tokenPrefix}-vivid)` : 'var(--text-muted)',
        }}
      />
      <span
        style={{
          maxWidth: '160px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: 'var(--type-caption-size)',
          fontWeight: 500,
          color: 'var(--text-secondary)',
        }}
      >
        {objective.title}
      </span>
    </button>
  );
}
